import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, CameraIcon, CheckIcon, LayoutGridIcon, FileTextIcon, ListChecksIcon, SendIcon, SquareIcon } from 'lucide-react';
import { ControlPointStatus, ControlSectionKind, QualityReport } from '../types/quality';
import { ControlTab } from '../components/quality/ControlTab';
import { DetailsTab } from '../components/quality/DetailsTab';
import { PhotosTab } from '../components/quality/PhotosTab';
import { ReportPreview } from '../components/quality/ReportPreview';
import { getStats } from '../utils/quality';
import { ProgressRing } from '../components/quality/ProgressRing';
interface QualityReportDetailProps {
  report: QualityReport;
  onBack: () => void;
  onChange: (report: QualityReport) => void;
}
type Tab = 'details' | 'control' | 'photos' | 'report';
const tabs: {
  id: Tab;
  label: string;
  icon: typeof FileTextIcon;
}[] = [{
  id: 'details',
  label: 'Detaljer',
  icon: FileTextIcon
}, {
  id: 'control',
  label: 'Kontrol',
  icon: ListChecksIcon
}, {
  id: 'photos',
  label: 'Fotos',
  icon: CameraIcon
}, {
  id: 'report',
  label: 'Rapport',
  icon: SendIcon
}];
const EASE = [0.23, 1, 0.32, 1] as const;
export function QualityReportDetail({
  report: savedReport,
  onBack,
  onChange
}: QualityReportDetailProps) {
  const [tab, setTab] = useState<Tab>('details');
  const [isSplit, setIsSplit] = useState(true);

  /**
   * Edits are held in a local draft so typing stays responsive, then written
   * back shortly after the user stops — one save instead of one per keystroke.
   */
  const [draft, setDraft] = useState(savedReport);
  const [isSaving, setIsSaving] = useState(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isDirty = useRef(false);

  // Adopt a different report, or pick up remote changes while idle.
  useEffect(() => {
    if (!isDirty.current) setDraft(savedReport);
  }, [savedReport]);
  useEffect(() => {
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, []);
  const onChangeDraft = (next: QualityReport) => {
    setDraft(next);
    isDirty.current = true;
    setIsSaving(true);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      onChange(next);
      isDirty.current = false;
      setIsSaving(false);
    }, 500);
  };
  const report = draft;
  const stats = getStats(report);
  const setPointStatus = (pointId: string, status: ControlPointStatus) => {
    onChangeDraft({
      ...report,
      sections: report.sections.map(section => ({
        ...section,
        points: section.points.map(point => point.id === pointId ? {
          ...point,
          status
        } : point)
      }))
    });
  };
  const savedLabel = isSaving ? 'Gemmer…' : 'Gemt';
  const renamePoint = (pointId: string, title: string) => {
    onChangeDraft({
      ...report,
      sections: report.sections.map(section => ({
        ...section,
        points: section.points.map(point => point.id === pointId ? {
          ...point,
          title
        } : point)
      }))
    });
  };
  const deletePoint = (pointId: string) => {
    onChangeDraft({
      ...report,
      sections: report.sections.map(section => ({
        ...section,
        points: section.points.filter(point => point.id !== pointId)
      })),
      photos: report.photos.map(photo => photo.controlPointId === pointId ? {
        ...photo,
        controlPointId: undefined
      } : photo)
    });
  };
  const addPoint = (sectionId: string, title: string) => {
    onChangeDraft({
      ...report,
      sections: report.sections.map(section => section.id === sectionId ? {
        ...section,
        points: [...section.points, {
          id: `kp-${Date.now()}`,
          title,
          status: 'pending' as ControlPointStatus,
          requiresPhoto: true
        }]
      } : section)
    });
  };
  const renameSection = (sectionId: string, title: string) => {
    onChangeDraft({
      ...report,
      sections: report.sections.map(section => section.id === sectionId ? {
        ...section,
        title
      } : section)
    });
  };
  const addSection = (title: string, kind: ControlSectionKind) => {
    onChangeDraft({
      ...report,
      sections: [...report.sections, {
        id: `sec-${Date.now()}`,
        title,
        kind,
        points: []
      }]
    });
  };
  const deleteSection = (sectionId: string) => {
    const section = report.sections.find(s => s.id === sectionId);
    if (!section || !window.confirm(`Slet "${section.title}" og alle dens kontrolpunkter?`)) return;
    const removedIds = section.points.map(p => p.id);
    onChangeDraft({
      ...report,
      sections: report.sections.filter(s => s.id !== sectionId),
      photos: report.photos.map(photo => photo.controlPointId && removedIds.includes(photo.controlPointId) ? {
        ...photo,
        controlPointId: undefined
      } : photo)
    });
  };
  const assignPhoto = (photoId: string, pointId: string | undefined) => {
    onChangeDraft({
      ...report,
      photos: report.photos.map(photo => photo.id === photoId ? {
        ...photo,
        controlPointId: pointId
      } : photo)
    });
  };

  // The report tab already shows the preview, so splitting it would duplicate.
  const showPreview = isSplit && tab !== 'report';
  const editor = <AnimatePresence mode="wait" data-id="element-1668" data-mp="1ongkqisanruu">
      <motion.div key={tab} initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.18,
      ease: EASE
    }} data-id="element-1669" data-mp="1oo8edo2bzfih">
        {tab === 'details' && <DetailsTab report={report} onToggleVisibility={() => onChangeDraft({
        ...report,
        visibleToCustomer: !report.visibleToCustomer
      })} onDescriptionChange={description => onChangeDraft({
        ...report,
        description
      })} onResponsibleChange={responsible => onChangeDraft({
        ...report,
        responsible
      })} onCompanyChange={nextCompany => onChangeDraft({
        ...report,
        company: nextCompany
      })} data-id="element-1670" data-mp="1jnkoj5pu94pu" />}
        {tab === 'control' && <ControlTab report={report} onSetPointStatus={setPointStatus} onRenamePoint={renamePoint} onDeletePoint={deletePoint} onAddPoint={addPoint} onRenameSection={renameSection} onDeleteSection={deleteSection} onAddSection={addSection} data-id="element-1671" data-mp="1eja0bsimpa44" />}
        {tab === 'photos' && <PhotosTab report={report} onAssignPhoto={assignPhoto} data-id="element-1672" data-mp="3pa08gsi4nacg" />}
        {tab === 'report' && <ReportPreview report={report} data-id="element-1673" data-mp="24u2639qnlom8" />}
      </motion.div>
    </AnimatePresence>;
  return <div data-id="element-1674" data-mp="29sly9rvubwn1">
      <button onClick={onBack} className="mb-5 inline-flex items-center gap-2 text-sm font-medium text-stone-500 transition-colors hover:text-stone-900" data-id="element-1675" data-mp="29sdfxkgy7z4g">
        <ArrowLeftIcon className="h-4 w-4" data-id="element-1676" data-mp="2jiowq7lqe2sr" />
        Tilbage
      </button>

      {/* Header */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4" data-id="element-1677" data-mp="2ofoulv8ffz8s">
        <div className="flex min-w-0 items-center gap-4" data-id="element-1678" data-mp="2shsgzewmwo8u">
          <ProgressRing percent={stats.percent} size={48} data-id="element-1679" data-mp="2skenrcbpazq0" />
          <div className="min-w-0" data-id="element-1680" data-mp="2xig8s2sot944">
            <div className="flex flex-wrap items-center gap-2.5" data-id="element-1681" data-mp="2xfbugclicwzq">
              <h1 className="text-2xl font-bold tracking-tight text-stone-900" data-id="element-1682" data-mp="32f6nomsr9yny">
                {report.reportId}
              </h1>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-stone-100 px-2.5 py-1 text-xs font-medium text-stone-600" data-id="element-1683" data-mp="135i2dua3ywm2">
                <span className={`h-1.5 w-1.5 rounded-full ${report.status === 'Åben' ? 'bg-emerald-500' : 'bg-stone-400'}`} data-id="element-1684" data-mp="39zijobrjxdn4" />
                {report.status}
              </span>
            </div>
            <p className="mt-0.5 truncate text-sm text-stone-500" data-id="element-1685" data-mp="3jrofxxou6h84">
              {report.customer.name} · {stats.completedPoints}/
              {stats.totalPoints} kontrolpunkter
            </p>
          </div>
        </div>

        <div className="flex flex-shrink-0 items-center gap-2" data-id="element-1686" data-mp="2m2l94n9nje1i">
          <span aria-live="polite" className={`inline-flex items-center gap-1.5 text-xs font-medium ${isSaving ? 'text-stone-400' : 'text-emerald-600'}`} data-id="element-1687" data-mp="2m4fx6ag7zm9t">
            {!isSaving && <CheckIcon className="h-3.5 w-3.5" data-id="element-1688" data-mp="g8llqnwwkxkx" />}
            {savedLabel}
          </span>
          <button onClick={() => setIsSplit(!isSplit)} aria-pressed={isSplit} className={`hidden items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors xl:inline-flex ${isSplit ? 'border-brand-200 bg-brand-50 text-brand-700' : 'border-stone-200 bg-white text-stone-600 hover:bg-stone-50'}`} data-id="element-1689" data-mp="baab4aysaj1n">
            {isSplit ? <LayoutGridIcon className="h-4 w-4" data-id="element-1690" data-mp="duq6mao3coq0" /> : <SquareIcon className="h-4 w-4" data-id="element-1691" data-mp="11l2ejljc6lgl" />}
            Delt visning
          </button>
          <button className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-700" data-id="element-1692" data-mp="16hsrhzzuh0xs">
            <SendIcon className="h-4 w-4" data-id="element-1693" data-mp="wt2fdvb3bwl2" />
            Send til kunde
          </button>
        </div>
      </div>

      {/* Title field */}
      <div className="mb-6 flex items-center gap-3 rounded-2xl border border-brand-200 bg-brand-50/60 p-3" data-id="element-1694" data-mp="1ki3m9e49yzo8">
        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-brand-600 text-white" data-id="element-1695" data-mp="1pb6iju0fsn84">
          <ListChecksIcon className="h-5 w-5" data-id="element-1696" data-mp="3lrbuz2vh0saz" />
        </span>
        <label className="min-w-0 flex-1" data-id="element-1697" data-mp="3qqy9dfn1celd">
          <span className="block text-[11px] font-semibold uppercase tracking-wider text-brand-700" data-id="element-1698" data-mp="3qpwt8wr5x1ws">
            Titel på kvalitetssikringen
          </span>
          <input value={report.title} onChange={e => onChangeDraft({
          ...report,
          title: e.target.value
        })} className="mt-0.5 w-full border-none bg-transparent p-0 text-base font-semibold text-stone-900 outline-none placeholder:text-stone-400" placeholder="Hvad handler opgaven om?" data-id="element-1699" data-mp="3gss5isho4ydv" />
        </label>
      </div>

      {/* Tabs */}
      <div role="tablist" aria-label="Kvalitetssikring sektioner" className="mb-6 flex gap-1 overflow-x-auto rounded-xl border border-stone-200 bg-white p-1" data-id="element-1700" data-mp="2y3vaunid03v4">
        {tabs.map(item => {
        const Icon = item.icon;
        const isActive = tab === item.id;
        return <button key={item.id} role="tab" aria-selected={isActive} onClick={() => setTab(item.id)} className={`relative flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${isActive ? 'text-brand-700' : 'text-stone-500 hover:text-stone-900'}`} data-id="element-1701" data-mp="25iylsytehrnl">
              {isActive && <motion.span layoutId="qaTab" className="absolute inset-0 -z-10 rounded-lg bg-brand-50" transition={{
            duration: 0.22,
            ease: EASE
          }} data-id="element-1702" data-mp="2x8a4wc0z0he2" />}
              <Icon className="h-4 w-4" data-id="element-1703" data-mp="375obzl107ttp" />
              {item.label}
            </button>;
      })}
      </div>

      {showPreview ? <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-2" data-id="element-1704" data-mp="325a0tu43w3rg">
          <div className="min-w-0" data-id="element-1705" data-mp="1zvjgy3ts0fso">{editor}</div>
          <div className="min-w-0 xl:sticky xl:top-24" data-id="element-1706" data-mp="23vaf4slmty78">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-stone-400" data-id="element-1707" data-mp="23vtmjfdnp470">
              Sådan ser kunden rapporten
            </p>
            <div className="max-h-[calc(100vh-11rem)] overflow-y-auto rounded-2xl" data-id="element-1708" data-mp="2dlwr8cu546l6">
              <ReportPreview report={report} data-id="element-1709" data-mp="2ikq360ts3pj8" />
            </div>
          </div>
        </div> : editor}
    </div>;
}